import { Component, type ReactNode } from "react";

interface Props { children: ReactNode; }
interface State { hasError: boolean; error?: Error; }

const RELOAD_FLAG = "tm-chunk-reload-attempted";

function isStaleChunkError(err: unknown): boolean {
  const msg = String((err as any)?.message ?? err ?? "");
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Failed to load module script") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed")
  );
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error) {
    // Stale-chunk errors: don't surface a scary error screen — just reload.
    // lazyWithRetry handles this at the import site, but if it slips past
    // (eager imports, vendor chunks) we catch it here as a safety net.
    if (isStaleChunkError(error) && typeof sessionStorage !== "undefined" && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    // Clear potentially corrupted cache
    const keys = Object.keys(localStorage).filter(k => k.startsWith("screentime-"));
    keys.forEach(k => localStorage.removeItem(k));
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          minHeight: "100vh", padding: "2rem", fontFamily: "system-ui, sans-serif",
          background: "#0f0f17", color: "#e2e2ea",
        }}>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Something went wrong</h1>
          <p style={{ color: "#9999aa", marginBottom: "1.5rem", textAlign: "center", maxWidth: 400 }}>
            The app hit an unexpected error. You can retry, or reset your local data to start fresh.
          </p>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button onClick={this.handleRetry} style={{
              padding: "0.5rem 1.25rem", borderRadius: "0.5rem",
              background: "#6366f1", color: "#fff", border: "none", cursor: "pointer", fontSize: "0.9rem",
            }}>Retry</button>
            <button onClick={this.handleReset} style={{
              padding: "0.5rem 1.25rem", borderRadius: "0.5rem",
              background: "#27272a", color: "#e2e2ea", border: "1px solid #3f3f46", cursor: "pointer", fontSize: "0.9rem",
            }}>Reset Cache</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
